import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, Search, LogIn, UserPlus, Truck, LayoutDashboard, Trophy, Home, ArrowLeft, Phone, Compass, HardHat, MapPin, LogOut, GraduationCap } from 'lucide-react';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [signupPickerOpen, setSignupPickerOpen] = useState(false);
  const navigate = useNavigate();
  
  // Check if user is logged in by checking localStorage
  const userEmail = localStorage.getItem('user_email');
  const userRoles = JSON.parse(localStorage.getItem('user_roles') || '[]');
  const activeRole = localStorage.getItem('active_role');
  const isLoggedIn = !!userEmail;
  const isPilotLoggedIn = localStorage.getItem('pilot_authed') === '1';
  const isAdminLoggedIn = localStorage.getItem('adminAuthenticated') === 'true';

  const handleLogout = () => {
    localStorage.removeItem('user_email');
    localStorage.removeItem('user_roles');
    localStorage.removeItem('active_role');
    localStorage.removeItem('user_data');
    localStorage.removeItem('user_role');
    navigate('/');
    window.location.reload();
  };

  // Determine dashboard link based on active role
  const getDashboardLink = () => {
    if (!isLoggedIn) return '/login';
    if (activeRole === 'carrier' || userRoles.includes('carrier')) {
      return `/carrier-dashboard?email=${encodeURIComponent(userEmail)}`;
    }
    if (activeRole === 'shipper' || userRoles.includes('shipper')) {
      return `/shipper-dashboard?email=${encodeURIComponent(userEmail)}`;
    }
    return '/login';
  };

  return (
    <>
      {/* Mobile top spacer — pushes header down so hamburger is accessible below phone notch/status bar */}
      <div className="2xl:hidden h-8 bg-[#1e3a5f]" />
      
      <header className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_truck-hub-2/artifacts/we3bquku_smallet%20logo%201.jpg" 
                alt="Truckn Send It" 
                className="h-10 sm:h-12"
              />
            </Link>
            
            {/* Desktop nav */}
            <nav className="hidden 2xl:flex items-center space-x-8">
              <Link to="/browse-loads" className="text-gray-700 hover:text-gray-900 font-medium transition-colors">
                Browse Loads
              </Link>
              <Link to="/transporters" className="text-gray-700 hover:text-gray-900 font-medium transition-colors">
                Transporters Ready to Load
              </Link>
              <Link to="/pilots-available" className="text-gray-700 hover:text-gray-900 font-medium transition-colors">
                Pilots Available
              </Link>
              <Link
                to="/compliance"
                className="ml-1 px-3 py-1.5 bg-red-600 text-white rounded-full text-sm font-semibold shadow-sm hover:bg-red-700 hover:shadow-md transition-all flex items-center gap-1.5"
                data-testid="header-compliance-cta"
              >
                <span className="inline-flex w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                Compliance
              </Link>
              <Link
                to="/insurance-quotes"
                className="px-3 py-1.5 bg-[#1e3a5f] text-white rounded-full text-sm font-semibold shadow-sm hover:bg-[#254d7f] hover:shadow-md transition-all flex items-center gap-1.5"
                data-testid="header-insurance-cta"
              >
                <span className="inline-flex w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                Insurance
              </Link>
              {isLoggedIn ? (
                <>
                  <Link to={getDashboardLink()} className="text-gray-700 hover:text-gray-900 font-medium transition-colors">
                    Back to Dashboard
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="text-red-600 hover:text-red-800 font-medium transition-colors flex items-center gap-1"
                    data-testid="header-logout-btn"
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="text-gray-700 hover:text-gray-900 font-medium transition-colors">
                    Log In
                  </Link>
                  <div className="relative group">
                    <button
                      className="flex items-center gap-1 bg-[#1e3a5f] hover:bg-[#254d7f] text-white font-semibold px-3 py-1.5 rounded-md shadow-sm transition-colors"
                      data-testid="desktop-signup-picker"
                    >
                      <UserPlus className="w-4 h-4" /> Sign Up ▾
                    </button>
                    <div className="absolute right-0 top-full mt-0 w-64 bg-white shadow-lg rounded-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 overflow-hidden">
                      <Link to="/signup?role=shipper" className="block px-4 py-3 hover:bg-blue-50 border-b border-gray-100" data-testid="desktop-signup-shipper">
                        <div className="text-sm font-semibold text-[#1e3a5f]">Shipper</div>
                        <div className="text-[11px] text-gray-500">I have loads to move</div>
                      </Link>
                      <Link to="/signup?role=carrier" className="block px-4 py-3 hover:bg-blue-50 border-b border-gray-100" data-testid="desktop-signup-carrier">
                        <div className="text-sm font-semibold text-[#1e3a5f]">Carrier</div>
                        <div className="text-[11px] text-gray-500">I move loads</div>
                      </Link>
                      <Link to="/signup?role=crane_operator" className="block px-4 py-3 hover:bg-orange-50 border-b border-gray-100 bg-orange-50/40" data-testid="desktop-signup-crane-operator">
                        <div className="text-sm font-semibold text-orange-800">Crane Operator</div>
                        <div className="text-[11px] text-orange-700">I operate cranes for lift jobs</div>
                      </Link>
                      <Link to="/pilot-registration" className="block px-4 py-3 hover:bg-blue-50" data-testid="desktop-signup-pilot">
                        <div className="text-sm font-semibold text-[#1e3a5f]">Pilot / Steerer</div>
                        <div className="text-[11px] text-gray-500">I escort oversize loads</div>
                      </Link>
                    </div>
                  </div>
                </>
              )}
            </nav>
            
            <div className="hidden 2xl:flex items-center space-x-4">
              <div className="relative group">
                <button className="text-gray-700 hover:text-gray-900 font-medium transition-colors px-3 py-2">
                  Services ▾
                </button>
                <div className="absolute left-0 top-full mt-0 w-56 bg-white shadow-lg rounded-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                  <Link to="/request-crane-quote" className="block px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-[#1e3a5f] rounded-t-lg">
                    Request a Crane Quote
                  </Link>
                <Link to="/pilots-available" className="block px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-[#1e3a5f]">
                    Book a Pilot / Steerer
                  </Link>
                  <Link to="/book-route-survey" className="block px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-[#1e3a5f]">
                    Book a Route Survey
                  </Link>
                  <Link to="/trailer-hire" className="block px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-[#1e3a5f]">
                    Truck &amp; Trailer Hire
                  </Link>
                  {isPilotLoggedIn && (
                    <>
                      <div className="border-t border-gray-100" />
                      <Link to="/pilot-dashboard" className="block px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-[#1e3a5f] rounded-b-lg">
                        Pilot Dashboard
                      </Link>
                    </>
                  )}
                </div>
              </div>
              <Button className="bg-red-600 hover:bg-red-700 text-white px-6" onClick={() => navigate('/request-quote')}>
                Post a Load
              </Button>
            </div>
            
            {/* Mobile: just logo + hamburger in header */}
            <div className="flex 2xl:hidden items-center gap-1">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 text-gray-700"
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
          
          {/* Mobile expanded menu (for secondary links) */}
          {mobileMenuOpen && (
            <div className="2xl:hidden mt-4 pb-4 border-t pt-4">
              <nav className="flex flex-col space-y-3">
                {/* Auth block hoisted to top so signups are visible on first open */}
                {isLoggedIn ? (
                  <>
                    <Link to={getDashboardLink()} className="text-gray-700 hover:text-gray-900 font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                      Back to Dashboard
                    </Link>
                    <button
                      onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                      className="text-red-600 hover:text-red-800 font-medium py-2 text-left flex items-center gap-1"
                      data-testid="mobile-logout-btn"
                    >
                      <LogOut className="w-4 h-4" />
                      Logout
                    </button>
                  </>
                ) : (
                  <div className="border border-gray-200 rounded-lg p-3 bg-blue-50/40">
                    <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Get Started</p>
                    <Link
                      to="/login"
                      onClick={() => setMobileMenuOpen(false)}
                      className="w-full flex items-center justify-between gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold px-3 py-2.5 rounded-md shadow-sm transition-colors"
                      data-testid="mobile-menu-login"
                    >
                      <span className="flex items-center gap-2">
                        <LogIn className="w-4 h-4" /> Log In
                      </span>
                      <span className="text-[11px] font-normal text-red-100">Already have an account?</span>
                    </Link>
                    <button
                      onClick={() => { setMobileMenuOpen(false); setSignupPickerOpen(true); }}
                      className="w-full mt-2 flex items-center justify-between gap-2 bg-[#1e3a5f] hover:bg-[#254d7f] text-white font-semibold px-3 py-2.5 rounded-md shadow-sm transition-colors"
                      data-testid="mobile-menu-signup-picker"
                    >
                      <span className="flex items-center gap-2">
                        <UserPlus className="w-4 h-4" /> Sign Up
                      </span>
                      <span className="text-[11px] font-normal text-blue-100">Shipper · Carrier · Crane · Pilot</span>
                    </button>
                  </div>
                )}
                <Button className="bg-red-600 hover:bg-red-700 text-white w-full" onClick={() => { navigate('/request-quote'); setMobileMenuOpen(false); }} data-testid="mobile-menu-post-load">
                  Post a Load
                </Button>
                <Link to="/browse-loads" className="text-gray-700 hover:text-gray-900 font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                  Browse Loads
                </Link>
                <Link to="/transporters" className="text-gray-700 hover:text-gray-900 font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                  Transporters Ready to Load
                </Link>
                <div className="border-t border-gray-200 pt-2 mt-1">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Services</p>
                  <Link to="/request-hotshot" className="text-red-600 font-bold py-2 block flex items-center gap-1" onClick={() => setMobileMenuOpen(false)}>
                    <Phone className="w-4 h-4" /> Hotshots (Urgent)
                  </Link>
                  <Link to="/request-crane-quote" className="text-gray-700 hover:text-gray-900 font-medium py-2 block" onClick={() => setMobileMenuOpen(false)}>
                    Request a Crane Quote
                  </Link>
                  <Link to="/pilots-available" className="text-gray-700 hover:text-gray-900 font-medium py-2 block" onClick={() => setMobileMenuOpen(false)}>
                    Book a Pilot / Steerer
                  </Link>
                  <Link to="/book-route-survey" className="text-gray-700 hover:text-gray-900 font-medium py-2 block" onClick={() => setMobileMenuOpen(false)}>
                    Book a Route Survey
                  </Link>
                  <Link to="/trailer-hire" className="text-gray-700 hover:text-gray-900 font-medium py-2 block" onClick={() => setMobileMenuOpen(false)}>
                    Truck &amp; Trailer Hire
                  </Link>
                  <Link to="/pilot-courses" className="text-gray-700 hover:text-gray-900 font-medium py-2 block" onClick={() => setMobileMenuOpen(false)}>
                    Pilot Courses
                  </Link>
                </div>
                <Link to="/truck-competition" className="text-gray-700 hover:text-gray-900 font-medium py-2 flex items-center gap-1" onClick={() => setMobileMenuOpen(false)}>
                  <span className="text-yellow-500">🏆</span> Truck Competition
                </Link>
                <Link to="/gallery" className="text-gray-700 hover:text-gray-900 font-medium py-2" onClick={() => setMobileMenuOpen(false)}>
                  Gallery
                </Link>
                <Link
                  to="/compliance"
                  className="my-2 px-3 py-2 bg-red-600 text-white rounded-full font-semibold flex items-center gap-2 justify-center"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-header-compliance-cta"
                >
                  <span className="inline-flex w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                  Compliance — we can help
                </Link>
                <Link
                  to="/insurance-quotes"
                  className="my-1 px-3 py-2 bg-[#1e3a5f] text-white rounded-full font-semibold flex items-center gap-2 justify-center"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-header-insurance-cta"
                >
                  <span className="inline-flex w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                  Insurance quotes
                </Link>
                {isPilotLoggedIn && (
                  <div className="border-t border-gray-200 pt-2 mt-1">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Pilots / Steerers</p>
                    <Link
                      to="/pilot-dashboard"
                      className="text-[#1e3a5f] font-semibold py-2 block flex items-center gap-1"
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid="mobile-menu-pilot-dashboard"
                    >
                      <LayoutDashboard className="w-4 h-4" /> Pilot Dashboard
                    </Link>
                  </div>
                )}
                <div className="border-t border-gray-200 pt-2 mt-1">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Admin</p>
                  <Link
                    to={isAdminLoggedIn ? '/admin' : '/admin-login'}
                    className="text-gray-700 hover:text-gray-900 font-medium py-2 block flex items-center gap-1"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid="mobile-menu-admin"
                  >
                    <LayoutDashboard className="w-4 h-4" />
                    {isAdminLoggedIn ? 'Admin Dashboard' : 'Admin Login'}
                  </Link>
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>
      
      <div className="bg-[#1e3a5f] text-white">
        <div className="container mx-auto px-4 sm:px-6 py-2 sm:py-3">
          <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-end text-xs sm:text-sm space-y-2 sm:space-y-0">
            <div className="flex flex-wrap items-center justify-center space-x-3 sm:space-x-6">
              <span className="hidden sm:inline">Australia Wide</span>
              <span className="text-gray-400 hidden sm:inline">|</span>
              <a href="tel:1300380700" className="hover:text-gray-300 transition-colors">1300 380 700</a>
              <span className="text-gray-400 hidden sm:inline">|</span>
              <a href="mailto:info@trucknsendit.com" className="hover:text-gray-300 transition-colors truncate">info@trucknsendit.com</a>
            </div>
          </div>
        </div>
      </div>
      
      {/* Action Buttons Bar */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 sm:px-6 py-3">
          <div className="grid grid-cols-2 sm:flex sm:flex-row sm:flex-wrap items-center justify-center gap-2 sm:gap-3">
            <Link 
              to="/pilots-available"
              className="bg-[#1e3a5f] hover:bg-[#2a4d7a] text-white font-bold px-3 sm:px-6 py-2.5 rounded-lg text-xs sm:text-sm tracking-wide transition-colors shadow-md flex items-center justify-center gap-1.5"
              data-testid="request-pilot-btn"
            >
              <Compass className="w-4 h-4" />
              REQUEST PILOT
            </Link>
            <Link 
              to="/request-hotshot"
              className="bg-red-600 hover:bg-red-700 text-white font-bold px-3 sm:px-6 py-2.5 rounded-lg text-xs sm:text-sm tracking-wide transition-colors shadow-md animate-pulse hover:animate-none flex items-center justify-center gap-1.5"
              data-testid="hotshots-btn"
            >
              <Phone className="w-4 h-4" />
              HOTSHOTS
            </Link>
            <Link 
              to="/request-crane-quote"
              className="bg-[#1e3a5f] hover:bg-[#2a4d7a] text-white font-bold px-3 sm:px-6 py-2.5 rounded-lg text-xs sm:text-sm tracking-wide transition-colors shadow-md flex items-center justify-center gap-1.5"
              data-testid="request-crane-btn"
            >
              <HardHat className="w-4 h-4" />
              REQUEST CRANE
            </Link>
            <Link 
              to="/book-route-survey"
              className="bg-[#1e3a5f] hover:bg-[#2a4d7a] text-white font-bold px-3 sm:px-6 py-2.5 rounded-lg text-xs sm:text-sm tracking-wide transition-colors shadow-md flex items-center justify-center gap-1.5"
              data-testid="request-route-survey-btn"
            >
              <MapPin className="w-4 h-4" />
              ROUTE SURVEY
            </Link>
            <Link 
              to="/trailer-hire"
              className="bg-[#1e3a5f] hover:bg-[#2a4d7a] text-white font-bold px-3 sm:px-6 py-2.5 rounded-lg text-xs sm:text-sm tracking-wide transition-colors shadow-md flex items-center justify-center gap-1.5"
              data-testid="trailer-hire-btn"
            >
              <Truck className="w-4 h-4" />
              TRUCK &amp; TRAILER HIRE
            </Link>
            <Link 
              to="/pilot-courses"
              className="bg-[#1e3a5f] hover:bg-[#2a4d7a] text-white font-bold px-3 sm:px-6 py-2.5 rounded-lg text-xs sm:text-sm tracking-wide transition-colors shadow-md flex items-center justify-center gap-1.5"
              data-testid="pilot-courses-btn"
            >
              <GraduationCap className="w-4 h-4" />
              PILOT COURSES
            </Link>
          </div>
        </div>
      </div>
      {/* Mobile bottom navigation bar - fixed, safe area aware */}
      <nav className="2xl:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }} data-testid="mobile-bottom-nav">
        <div className="flex items-center justify-around px-1 pt-2 pb-1">
          <button
            onClick={() => navigate(-1)}
            className="flex flex-col items-center py-1 px-2 text-gray-600 hover:text-[#1e3a5f] transition-colors"
            data-testid="mobile-back"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-[10px] mt-0.5">Back</span>
          </button>
          <button
            onClick={() => navigate('/browse-loads')}
            className="flex flex-col items-center py-1 px-2 text-gray-600 hover:text-[#1e3a5f] transition-colors"
            data-testid="mobile-browse-loads"
          >
            <Search className="w-5 h-5" />
            <span className="text-[10px] mt-0.5">Loads</span>
          </button>
          <button
            onClick={() => navigate('/transporters')}
            className="flex flex-col items-center py-1 px-2 text-gray-600 hover:text-[#1e3a5f] transition-colors"
            data-testid="mobile-transporters"
          >
            <Truck className="w-5 h-5" />
            <span className="text-[10px] mt-0.5">Transporters</span>
          </button>
          <button
            onClick={() => navigate('/request-quote')}
            className="flex flex-col items-center py-1 px-2 text-red-600 hover:text-red-700 transition-colors"
            data-testid="mobile-post-load"
          >
            <div className="bg-red-600 rounded-full p-2 -mt-4 shadow-lg">
              <Truck className="w-5 h-5 text-white" />
            </div>
            <span className="text-[10px] mt-0.5 font-semibold">Post</span>
          </button>
          {isLoggedIn ? (
            <button
              onClick={() => navigate(getDashboardLink())}
              className="flex flex-col items-center py-1 px-2 text-gray-600 hover:text-[#1e3a5f] transition-colors"
              data-testid="mobile-dashboard"
            >
              <LayoutDashboard className="w-5 h-5" />
              <span className="text-[10px] mt-0.5">Dashboard</span>
            </button>
          ) : (
            <button
              onClick={() => navigate('/login')}
              className="flex flex-col items-center py-1 px-2 text-gray-600 hover:text-[#1e3a5f] transition-colors"
              data-testid="mobile-login"
            >
              <LogIn className="w-5 h-5" />
              <span className="text-[10px] mt-0.5">Log In</span>
            </button>
          )}
          {isLoggedIn ? (
            <button
              onClick={handleLogout}
              className="flex flex-col items-center py-1 px-2 text-red-500 hover:text-red-700 transition-colors"
              data-testid="mobile-bottom-logout"
            >
              <LogOut className="w-5 h-5" />
              <span className="text-[10px] mt-0.5">Logout</span>
            </button>
          ) : (
            <button
              onClick={() => setSignupPickerOpen(true)}
              className="flex flex-col items-center py-1 px-2 text-gray-600 hover:text-[#1e3a5f] transition-colors"
              data-testid="mobile-signup"
            >
              <UserPlus className="w-5 h-5" />
              <span className="text-[10px] mt-0.5">Sign Up</span>
            </button>
          )}
        </div>
      </nav>

      {/* Mobile Sign Up role picker modal */}
      {signupPickerOpen && (
        <div
          className="2xl:hidden fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center"
          onClick={() => setSignupPickerOpen(false)}
          data-testid="mobile-signup-picker-backdrop"
        >
          <div
            className="bg-white w-full sm:max-w-sm sm:mx-4 rounded-t-2xl sm:rounded-2xl p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold text-[#1e3a5f]">Sign up as…</h3>
              <button
                onClick={() => setSignupPickerOpen(false)}
                className="text-gray-500 hover:text-gray-800"
                data-testid="mobile-signup-picker-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-4">Choose the role that best fits you.</p>
            <div className="space-y-2">
              <button
                onClick={() => { setSignupPickerOpen(false); navigate('/signup?role=shipper'); }}
                className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 hover:border-[#1e3a5f] hover:bg-blue-50 transition"
                data-testid="mobile-signup-shipper"
              >
                <div className="font-semibold text-[#1e3a5f]">Shipper</div>
                <div className="text-xs text-gray-500">I have loads to move</div>
              </button>
              <button
                onClick={() => { setSignupPickerOpen(false); navigate('/signup?role=carrier'); }}
                className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 hover:border-[#1e3a5f] hover:bg-blue-50 transition"
                data-testid="mobile-signup-carrier"
              >
                <div className="font-semibold text-[#1e3a5f]">Carrier</div>
                <div className="text-xs text-gray-500">I move loads</div>
              </button>
              <button
                onClick={() => { setSignupPickerOpen(false); navigate('/signup?role=crane_operator'); }}
                className="w-full text-left px-4 py-3 rounded-lg border border-orange-300 bg-orange-50 hover:border-orange-500 transition"
                data-testid="mobile-signup-crane-operator"
              >
                <div className="font-semibold text-orange-800">Crane Operator</div>
                <div className="text-xs text-orange-700">I operate cranes for lift jobs</div>
              </button>
              <button
                onClick={() => { setSignupPickerOpen(false); navigate('/pilot-registration'); }}
                className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 hover:border-[#1e3a5f] hover:bg-blue-50 transition"
                data-testid="mobile-signup-pilot"
              >
                <div className="font-semibold text-[#1e3a5f]">Pilot / Steerer</div>
                <div className="text-xs text-gray-500">I escort oversize loads</div>
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Spacer so page content isn't hidden behind bottom nav on mobile */}
      <div className="2xl:hidden h-16" />
    </>
  );
};

export default Header;
